import 'moment/dist/locale/de-ch';
import 'moment/dist/locale/fr-ch';
import 'moment/dist/locale/en-gb';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { LicenseInfo, muiXTelemetrySettings } from '@mui/x-license';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { enableMapSet, setAutoFreeze } from 'immer';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import invariant from 'tiny-invariant';

import '@@utils/logRender';
import '@@lib/i18n/i18n';
import queryClient from '@@api/queryClient';
import config from '@@config';
import { PageLayoutProvider } from '@@containers/App/PageLayout/PageLayoutContext';
import { SnackbarItem, SnackbarProvider } from '@@containers/Snackbar';
import { createAppRouter } from '@@router/router';
import styles from '@@scripts/themes/mui/GlobalStyles';
import theme from '@@themes/mui';
import LocalizedThemeProvider from '@@themes/mui/LocalizedThemeProvider';

import { useSettingsStore } from './store/settings/useSettingsStore';
import { onUILocaleChanged } from './store/settings/utils';
import { initializeDataDogRUM } from './utils/dataDog';

const ENVS = {
    MODE: config.mode,
    CDII_ENV: config.env,
    API_ENV: config.apiEnv,
};

window.VERSION = VERSION;
window.ENVS = ENVS;

LicenseInfo.setLicenseKey(config.muiLicenseKey);
muiXTelemetrySettings.disableTelemetry();

// We need to disable autoFreeze, because the Article Buddy zustand.js store, stores range refs in its state,
// and those refs must be mutable, otherwise the editor would throw an error when trying to update the selection.
setAutoFreeze(false);

// Article Buddy zustand.js store uses `Set`s to store indexes.
enableMapSet();

initializeDataDogRUM();

// Call onUILocaleChanged initially with current uiLocale
const initialUiLocale = useSettingsStore.getState().uiLocale;
if (initialUiLocale) {
    onUILocaleChanged(initialUiLocale);
}

// Setup subscription to uiLocale changes
useSettingsStore.subscribe(
    (state) => state.uiLocale,
    (newLocale, prevLocale) => {
        if (newLocale && newLocale !== prevLocale) {
            onUILocaleChanged(newLocale);
        }
    },
);

const container = document.querySelector('.app-container');
invariant(container, 'Missing `.app-container` element');

const router = createAppRouter();

const root = createRoot(container);

const helmetContext = {};

root.render(
    <QueryClientProvider client={queryClient}>
        <HelmetProvider context={helmetContext}>
            <LocalizedThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DndProvider backend={HTML5Backend}>
                        <PageLayoutProvider>
                            <SnackbarProvider
                                anchorOrigin={{
                                    horizontal: 'right',
                                    vertical: 'top',
                                }}
                                // The native `autoHideDuration` option is buggy
                                // (snackbar is hidden too early) and we also want to
                                // display a progress bar. Therefore we use our custom
                                // implementation.
                                autoHideDuration={null}
                                maxSnack={config.snackbar.maxSnack}
                                preventDuplicate
                                Components={{
                                    error: SnackbarItem,
                                    warning: SnackbarItem,
                                    success: SnackbarItem,
                                    info: SnackbarItem,
                                }}
                            >
                                <CssBaseline />
                                <GlobalStyles styles={styles} />

                                {!config.isVisualTest && (
                                    <TanStackDevtools
                                        plugins={[
                                            {
                                                name: 'TanStack Query',
                                                render: <ReactQueryDevtoolsPanel />,
                                            },
                                            {
                                                name: 'TanStack Router',
                                                render: (
                                                    <TanStackRouterDevtoolsPanel router={router} />
                                                ),
                                            },
                                        ]}
                                    />
                                )}

                                <RouterProvider router={router} />
                            </SnackbarProvider>
                        </PageLayoutProvider>
                    </DndProvider>
                </LocalizationProvider>
            </LocalizedThemeProvider>
        </HelmetProvider>
    </QueryClientProvider>,
);
